<template lang="pug">
main
  u-page-hero(
    :ui="{ container: 'pb-0 sm:pb-0 lg:py-0', title: 'lg:mt-16', links: 'lg:mb-16', description: 'text-balance' }"
    orientation="horizontal"
  )
    template(#title)
      | We Build Things People
      span.text-primary  Actually Use.
    template(#description)
      | We're a small Helsinki studio building consumer products people come back to. Right now that means Kirjaviisas, a curated book club for Finland, and Poemly, a poetry app for everyday listening and reading.
    template(#links)
      u-button(
        v-for="link of heroLinks"
        :key="link.label"
        v-bind="link"
        size="xl"
      )
      .w-full.my-6
        u-separator(
          class="w-1/2"
          type="dashed"
        )
      .flex.flex-col.gap-4
        motion(
          v-for="(feature, index) in heroFeatures"
          :key="feature.title"
          :initial="{ opacity: 0, transform: 'translateX(-10px)' }"
          :while-in-view="{ opacity: 1, transform: 'translateX(0)' }"
          :transition="{ delay: 0.2 + 0.4 * index }"
          :in-view-options="{ once: true }"
          as-child
        )
          u-page-feature.opacity-0(v-bind="feature")

    sky-bg(is-index)

    .w-full.overflow-hidden(
      class="h-[344px] lg:h-full lg:relative lg:min-h-[calc(100vh-var(--ui-header-height)-1px)]"
    )
      u-marquee(
        :overlay="false"
        :ui="{ root: '[--gap:--spacing(4)] [--duration:80s] border-default absolute w-full left-0 border-y lg:border-x lg:border-y-0 lg:w-[calc(50%-6px)] 2xl:max-w-[320px] lg:flex-col', content: 'lg:w-auto lg:flex-col lg:animate-[marquee-vertical_var(--duration)_linear_infinite] lg:rtl:animate-[marquee-vertical-rtl_var(--duration)_linear_infinite] lg:h-fit' }"
        pause-on-hover
      )
        u-link.relative(
          v-for="app of heroApps.slice(0, 10)"
          :key="app.to"
          :to="app.to"
          target="_new"
          class=" group/link w-[290px] xl:w-[330px] 2xl:w-[320px]"
          tabindex="-1"
        )
          u-color-mode-image.transition-transform.w-full(
            :light="app.imageLight"
            :dark="app.imageDark"
            :alt="`${app.title} preview`"
            class="hover:scale-105"
            height="163"
            format="webp"
            loading="lazy"
          )
          u-badge.hidden.absolute.top-2.left-8.opacity-0.transition-all.duration-300.pointer-events-none(
            :label="app.title"
            color="neutral"
            variant="outline"
            size="md" 
            class="lg:block mx-auto group-hover/link:opacity-100 -translate-y-2 group-hover/link:translate-y-0"
          )
      u-marquee(
        :overlay="false"
        :ui="{ root: '[--gap:--spacing(4)] [--duration:80s] border-default absolute w-full mt-[180px] left-0 border-y lg:mt-auto lg:left-auto lg:border-y-0 lg:border-x lg:w-[calc(50%-6px)] 2xl:max-w-[320px] lg:right-0 lg:flex-col', content: 'lg:w-auto lg:flex-col lg:animate-[marquee-vertical_var(--duration)_linear_infinite] lg:rtl:animate-[marquee-vertical-rtl_var(--duration)_linear_infinite] lg:h-fit lg:[animation-direction:reverse]' }"
        pause-on-hover
        reverse
      )
        u-link.relative(
          v-for="app of heroApps.slice(10)"
          :key="app.to"
          :to="app.to"
          target="_new"
          class=" group/link w-[290px] xl:w-[330px] 2xl:w-[320px]"
          tabindex="-1"
        )
          u-color-mode-image.transition-transform.w-full(
            :light="app.imageLight"
            :dark="app.imageDark"
            :alt="`${app.title} preview`"
            class="hover:scale-105"
            height="163"
            format="webp"
            loading="lazy"
          )
          u-badge.hidden.absolute.top-2.left-8.opacity-0.transition-all.duration-300.pointer-events-none(
            :label="app.title"
            color="neutral"
            variant="outline"
            size="md" 
            class="lg:block mx-auto group-hover/link:opacity-100 -translate-y-2 group-hover/link:translate-y-0"
          )

  u-separator

  u-page-section(
    :ui="{ container: 'lg:py-16' }"
    class="bg-elevated/25"
  )
    ul.grid.grid-cols-1.gap-6(
      class="sm:grid-cols-2 lg:grid-cols-3 lg:gap-8 xl:gap-y-10"
    )
      motion.flex.items-start.gap-x-3.relative.group(
        v-for="(feature, index) in heroSubFeatures"
        :key="feature.title"
        :initial="{ opacity: 0, transform: 'translateY(10px)' }"
        :while-in-view="{ opacity: 1, transform: 'translateY(0)' }"
        :transition="{ delay: 0.1 * index }"
        :in-view-options="{ once: true }"
        as="li"
      )
        .relative.p-3
          svg.absolute.inset-0(
            viewBox="0 0 44 44"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          )
            line(x1="6.5" x2="6.5" y2="44" stroke="var(--ui-border)")
            line(x1="38.5" x2="38.5" y2="44" stroke="var(--ui-border)")
            line(y1="5.5" x2="44" y2="5.5" stroke="var(--ui-border)")
            line(y1="37.5" x2="44" y2="37.5" stroke="var(--ui-border)")
            circle(cx="6.53613" cy="5.45508" r="1.5" fill="var(--ui-border-accented)")
            circle(cx="38.5957" cy="5.45508" r="1.5" fill="var(--ui-border-accented)")
            circle(cx="6.53711" cy="37.4551" r="1.5" fill="var(--ui-border-accented)")
            circle(cx="38.5957" cy="37.4551" r="1.5" fill="var(--ui-border-accented)")
          u-icon.size-5.shrink-0(:name="feature.icon")
        .flex.flex-col
          h2.font-medium.text-highlighted.inline-flex.items-center.gap-x-1
            | {{ feature.title }}
            u-icon.size-4.shrink-0.opacity-0.transition-all.duration-200(
              v-if="feature.to"
              name="lucide:arrow-right"
              class="group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0"
            )
          p.text-sm.text-muted {{ feature.description }}
          
  u-separator

  u-page-section#apps(
    title="Three Books a Month, Chosen for You."
    description="Kirjaviisas is Finland’s curated book club. Pick a theme, we choose the books, and Posti brings the package to your door: mostly carefully chosen used books, with new titles in select packs."
    :features="kirjaviisasFeatures"
    :links="kirjaviisasLinks"
    orientation="horizontal"
  )
    .aspect-square.w-full.overflow-hidden.rounded-2xl.bg-white.shadow-xl
      u-color-mode-image.h-full.w-full.object-contain(
        light="/media/kirjaviisas-hero-books-box.png"
        dark="/media/kirjaviisas-hero-books-box.png"
        alt="Kirjaviisas"
        loading="lazy"
      )

  u-separator

  u-page-section(
    title="Poetry for People Who Don't Think They Like Poetry."
    description="Poemly brings the greatest poems ever written to your pocket, beautifully designed, professionally narrated, and curated for people who want the experience without the homework."
    :features="poemlyFeatures"
    :links="poemlyLinks"
    orientation="horizontal"
  )
    u-color-mode-image.w-full.rounded-2xl.shadow-xl(
      light="/media/poemly-logo.png"
      dark="/media/poemly-logo.png"
      alt="Poemly"
      loading="lazy"
    )
</template>

<script>
export default {
  name: 'PageIndex',
  data: () => ({
    heroLinks: [
      {
        label: 'Discover Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        target: '_blank',
        trailingIcon: 'i-lucide-arrow-right'
      }
    ],
    heroFeatures: [
      {
        title: 'Consumer first',
        description:
          'We build for real people with real habits: reading, listening, discovering. If it is not clear and useful, we are not done.',
        icon: 'lucide:shopping-cart'
      },
      {
        title: 'Small team, sharp focus',
        description:
          'We do not scale headcount. We scale ideas. Most of our time goes into Kirjaviisas, so the product stays focused and keeps improving.',
        icon: 'lucide:users'
      },
      {
        title: 'Built for the long run',
        description:
          'No VC pressure, no growth-at-all-costs. We build sustainable products that stick around and earn trust over time.',
        icon: 'lucide:clock'
      }
    ],
    heroApps: [
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/0e429c60-efaf-4eef-addb-388ea5f757dd.jpeg',
        imageDark:
          '/media/hero-illustrations/0e429c60-efaf-4eef-addb-388ea5f757dd.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/0f79f8df-ca88-4469-8c8b-5766928900b6.jpeg',
        imageDark:
          '/media/hero-illustrations/0f79f8df-ca88-4469-8c8b-5766928900b6.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/01cb2904-d758-4fd5-9732-4b9a0e84f966.jpeg',
        imageDark:
          '/media/hero-illustrations/01cb2904-d758-4fd5-9732-4b9a0e84f966.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/2c97101b-b942-4615-ad63-cd7ebeb9e190.jpeg',
        imageDark:
          '/media/hero-illustrations/2c97101b-b942-4615-ad63-cd7ebeb9e190.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/2d4d9e9c-3f97-435c-9e7a-d835f894a524.jpeg',
        imageDark:
          '/media/hero-illustrations/2d4d9e9c-3f97-435c-9e7a-d835f894a524.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/2ee5de0f-069c-4786-8089-d10400da2c37.jpeg',
        imageDark:
          '/media/hero-illustrations/2ee5de0f-069c-4786-8089-d10400da2c37.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/07f7dde3-9cd8-4996-b065-ccb54ee609ea.jpeg',
        imageDark:
          '/media/hero-illustrations/07f7dde3-9cd8-4996-b065-ccb54ee609ea.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/11affde5-aad5-40f7-aff4-54e2977a239e.jpeg',
        imageDark:
          '/media/hero-illustrations/11affde5-aad5-40f7-aff4-54e2977a239e.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/22a90b2b-2f0a-4b24-bccb-55db3fd7b5a0.jpeg',
        imageDark:
          '/media/hero-illustrations/22a90b2b-2f0a-4b24-bccb-55db3fd7b5a0.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/39b50101-d730-44c7-a577-1c06ea76702d.jpeg',
        imageDark:
          '/media/hero-illustrations/39b50101-d730-44c7-a577-1c06ea76702d.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/42fa2899-069e-44fd-aa67-ae0a3ea3f757.jpeg',
        imageDark:
          '/media/hero-illustrations/42fa2899-069e-44fd-aa67-ae0a3ea3f757.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/45ac3d9a-0284-44e1-ba64-4fc3ea6c0472.jpeg',
        imageDark:
          '/media/hero-illustrations/45ac3d9a-0284-44e1-ba64-4fc3ea6c0472.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/078c4295-6bff-4440-8c14-4dfb5a38c166.jpeg',
        imageDark:
          '/media/hero-illustrations/078c4295-6bff-4440-8c14-4dfb5a38c166.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/349c6ce7-034e-4c8d-828f-3e3bc70fcd62.jpeg',
        imageDark:
          '/media/hero-illustrations/349c6ce7-034e-4c8d-828f-3e3bc70fcd62.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/405ba4e1-3d2f-48fb-a593-4a7231d8deda.jpeg',
        imageDark:
          '/media/hero-illustrations/405ba4e1-3d2f-48fb-a593-4a7231d8deda.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/406c82b2-0eb7-4529-aee3-61f0e6c54944.jpeg',
        imageDark:
          '/media/hero-illustrations/406c82b2-0eb7-4529-aee3-61f0e6c54944.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/482e11a0-c9b6-4d2f-9557-87a10e47772b.jpeg',
        imageDark:
          '/media/hero-illustrations/482e11a0-c9b6-4d2f-9557-87a10e47772b.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/493eb648-cd06-4a2a-8586-b87ff4e00bfc.jpeg',
        imageDark:
          '/media/hero-illustrations/493eb648-cd06-4a2a-8586-b87ff4e00bfc.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/1805a559-f3b6-452c-ab1e-7640a65686fb.jpeg',
        imageDark:
          '/media/hero-illustrations/1805a559-f3b6-452c-ab1e-7640a65686fb.jpeg'
      },
      {
        title: 'Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        imageLight:
          '/media/hero-illustrations/060548f5-fe88-4024-aaeb-d43902a784cb.jpeg',
        imageDark:
          '/media/hero-illustrations/060548f5-fe88-4024-aaeb-d43902a784cb.jpeg'
      }
    ],
    heroSubFeatures: [
      {
        title: 'Bootstrapped by design',
        description:
          'No investors means no one telling us to compromise the product for a growth metric. We move on our own terms.',
        icon: 'lucide:sport-shoe'
      },
      {
        title: 'Obsessive about quality',
        description:
          "We'd rather ship one thing that's genuinely good than five things that are fine. Fine doesn't get word of mouth.",
        icon: 'lucide:sparkles'
      },
      {
        title: 'AI where it helps',
        description:
          'We use AI tooling to move faster as a tiny team, for operations and product work, not as a gimmick on the homepage.',
        icon: 'lucide:wand'
      },
      {
        title: 'Products, not platforms',
        description:
          'Whether it is a Finnish book club or a mobile app, we ship focused products people can understand in a minute.',
        icon: 'lucide:tablet-smartphone'
      },
      {
        title: 'Data stays yours',
        description:
          "We don't sell user data, run ads, or monetise attention. We charge fair prices for products worth paying for.",
        icon: 'lucide:shield-check'
      },
      {
        title: 'Built in Helsinki',
        description:
          'Designed and run from Finland, where good design and straight talking are part of the culture.',
        icon: 'emojione-v1:flag-for-finland'
      }
    ],
    kirjaviisasFeatures: [
      {
        title: 'Choose a theme',
        description:
          'Classics, crime, kids, nonfiction, and more. Tell us what you like, or let us surprise you.',
        icon: 'lucide:book-marked'
      },
      {
        title: 'We curate the books',
        description:
          'Our team picks three books for each month (or a four-book pack every other month). We track what you already received, so you do not get the same title twice.',
        icon: 'lucide:sparkles'
      },
      {
        title: 'Delivered to your door',
        description:
          'Packed and shipped in Finland. Follow the delivery from your account once the package is on the way.',
        icon: 'lucide:package'
      }
    ],
    kirjaviisasLinks: [
      {
        label: 'Visit Kirjaviisas',
        to: 'https://kirjaviisas.fi',
        target: '_blank',
        trailingIcon: 'i-lucide-arrow-right'
      }
    ],
    poemlyFeatures: [
      {
        title: 'Read it',
        description:
          'Hundreds of classic poems in a clean, typography-first reading experience. No ads, no clutter: just the words.',
        icon: 'lucide:glasses'
      },
      {
        title: 'Hear it',
        description:
          'Every poem narrated by a professional AI voice. Like an audiobook, but for the most concentrated writing in the English language.',
        icon: 'lucide:ear'
      },
      {
        title: 'Feel it',
        description:
          'A new poem every day, curated collections by mood, and a discover feed that meets you where you are, not where your English teacher wanted you to be.',
        icon: 'lucide:heart'
      }
    ],
    poemlyLinks: [
      {
        label: 'Start your free trial',
        to: 'https://poemly.app',
        target: '_blank',
        trailingIcon: 'i-lucide-arrow-right'
      }
    ]
  })
}
</script>

<style lang="stylus" scoped></style>
